import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterBar from '@/pages/category/components/FilterBar';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import {
  CATEGORY_META,
  PAGE_SIZE,
  collectFacets,
  getByCategory,
  sortContents,
} from '@/utils/content';

export default function Category() {
  const { type = '' } = useParams();
  const meta = CATEGORY_META[type];

  const baseList = useMemo(() => getByCategory(type), [type]);
  const facets = useMemo(() => collectFacets(baseList), [baseList]);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [sort, setSort] = useState<'updated' | 'hot'>('updated');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort('updated');
    setPage(1);
  }, [type]);

  useEffect(() => {
    if (meta) {
      document.title = `${meta.h1} - 中文字幕一区二区三区四虎在线`;
    }
  }, [meta]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, [type, selectedGenres, selectedRegions, selectedYears, sort, page]);

  const toggle = (list: string[], setter: (v: string[]) => void, value: string) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = baseList.filter((item) => {
      const genreOk = selectedGenres.length === 0 || item.genres.some((g) => selectedGenres.includes(g));
      const regionOk = selectedRegions.length === 0 || selectedRegions.includes(item.region);
      const yearOk = selectedYears.length === 0 || selectedYears.includes(String(item.year));
      return genreOk && regionOk && yearOk;
    });
    return sortContents(result, sort);
  }, [baseList, selectedGenres, selectedRegions, selectedYears, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort('updated');
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="未找到对应的影视分类，请从下方重新选择频道。"
          secondaryLabel="返回首页"
          secondaryTo="/"
        />
      </div>
    );
  }

  const activeCount = selectedGenres.length + selectedRegions.length + selectedYears.length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="text-sm text-foreground-500 mb-4" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <span className="mx-2">›</span>
        <span className="text-foreground-900">{meta.name}频道</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
          {meta.h1} - 中文字幕一区二区三区四虎在线
        </h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl">{meta.desc}</p>
      </header>

      <FilterBar
        genres={facets.genres}
        regions={facets.regions}
        years={facets.years}
        selectedGenres={selectedGenres}
        selectedRegions={selectedRegions}
        selectedYears={selectedYears}
        sort={sort}
        onToggleGenre={(v) => toggle(selectedGenres, setSelectedGenres, v)}
        onToggleRegion={(v) => toggle(selectedRegions, setSelectedRegions, v)}
        onToggleYear={(v) => toggle(selectedYears, setSelectedYears, v)}
        onSortChange={(v) => { setSort(v); setPage(1); }}
        onReset={resetAll}
        activeCount={activeCount}
      />

      <div className="mt-6 text-sm text-foreground-600">
        共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部{meta.name}
      </div>

      {loading ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <div key={i} className="rounded-lg overflow-hidden border border-background-200">
              <div className="w-full aspect-[2/3] bg-background-200 animate-pulse"></div>
              <div className="p-3 space-y-2">
                <div className="h-4 bg-background-200 rounded animate-pulse"></div>
                <div className="h-3 w-2/3 bg-background-200 rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      ) : pageItems.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-film-line"
            title="暂无相关影视资源"
            description="没有符合当前筛选条件的影视作品，试试调整筛选条件或重置筛选。"
            actionLabel="重置筛选"
            onAction={resetAll}
            secondaryLabel="返回首页"
            secondaryTo="/"
          />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}